// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductGridContainer, { ProductGridContainerStyle } from "@conklin/App/Components/Product/ProductGridContainer";
import { mergeWithCss } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showAddToCartButton?: boolean;
    showCompare?: boolean;
    showAddToCartActions?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    containerClassName?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices && state.context.showPricing,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps> & HasHistory;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    priceAndQtyContainer?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    priceContainer?: InjectableCss;
    productFields?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productImageStyles?: ProductListProductImageStyles;
    productAvailableStyles?: ProductAvailabilityStyles;
    addToCartActions?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartButton,
    showAddToCartActions, // Show Add to cart + uom + qty
    showCompare = false,
    productContext,
    onAddToCartClick,
    containerClassName,
    canSeePrices,
    history,
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const { product, productInfo } = productContext;

    const handleVariantSelect = () => {
        history.push(productInfo.productDetailPath);
    };
    return (
        <ProductGridContainer
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
            data-test-selector="product-price-selector"
        >
            <ProductGridContainer.Info>
                {showAvailability && showPrice && (
                    <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />
                )}
                <StyledWrapper {...styles.priceAndQtyContainer}>
                    {showPrice && canSeePrices && (
                        <StyledWrapper {...styles.priceContainer}>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={true}
                                showPack={false}
                                {...styles.productPriceStyles}
                            />
                        </StyledWrapper>
                    )}
                    {showAddToCartButton && (
                        <>
                            <StyledWrapper {...styles.productFields}>
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        sizeVariant="small"
                                    />
                                )}
                                <ProductQuantityOrdered />
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </ProductGridContainer.Info>
            <ProductGridContainer.AddToCartActions>
                {showAvailability && !showPrice && (
                    <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />
                )}
                {showAddToCartButton && (
                    <>
                        {!product?.isVariantParent ? (
                            <ProductAddToCartButton
                                extendedStyles={styles.addToCartButton}
                                onAddToCartClick={onAddToCartClick}
                            />
                        ) : (
                            <Button {...styles.addToCartButton} onClick={handleVariantSelect}>
                                {translate("Choose Selection")}
                            </Button>
                        )}
                    </>
                )}
                {showAddToCartActions && (
                    <>
                        <StyledWrapper {...styles.addToCartActions}>
                            {!product.isVariantParent && (
                                <StyledWrapper {...styles.productFields} className="qty-section">
                                    <ProductQuantityOrdered />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            sizeVariant="small"
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                            {!product.isVariantParent ? (
                                <ProductAddToCartButton
                                    extendedStyles={styles.addToCartButton}
                                    onAddToCartClick={onAddToCartClick}
                                    data-test-selector="addToCartBtn"
                                />
                            ) : (
                                <Button
                                    {...styles.addToCartButton}
                                    onClick={handleVariantSelect}
                                    data-test-selector="chooseSelection"
                                >
                                    {translate("Choose Selection")}
                                </Button>
                            )}
                        </StyledWrapper>
                    </>
                )}
            </ProductGridContainer.AddToCartActions>
            <ProductGridContainer.AddToList>
                <ProductListProductImage
                    extendedStyles={styles.productImageStyles}
                    showImage={false}
                    showCompare={showCompare}
                />
                {showAddToList && <ProductAddToListLink extendedStyles={styles.addToListLink} />}
            </ProductGridContainer.AddToList>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(withHistory(ProductPriceAndActions)));
